/**
 * Der Artikelstreifen einer Zeitstrahl-Zeile - ein Knopf, der die Positionen der Bestellung als
 * Bildkacheln zeigt und beim Klick das `PositionenPopup` mit allen Positionen oeffnet.
 *
 * OHNE VORBILD IM ORIGINAL (Auftrag 2, "Zeitstrahlseite: sprechende Zeile, Artikelstreifen,
 * Positions-Popup"): `index.html` zeigt an dieser Stelle keine Artikel. Sinngemaesses Vorbild ist
 * `zeichneStreifen` im Schwesterprojekt web_wms
 * (`apps/templates/includes/_artikelkacheln_scripts.html:125`, CSS `.overview-thumb*` in
 * `apps/static/src/input.css:420-470`) - dort Django/Vanilla-JS gegen eine eigene API-Antwort
 * (`shipment_content`), hier React 19 gegen die bereits geladenen `order.items[]`. NICHT
 * zeilengleich uebernommen: web_wms fasst mehrere Kartons zu EINER Artikelliste zusammen und
 * sortiert nach Menge, hier gibt es nur die eine Liste `order.items` in ihrer JTL-Reihenfolge
 * (`sortierung`) - keine Zusammenfassung, keine Neusortierung.
 *
 * ================= Die Grenze: 4 alle, ab 5 drei plus "+X" =================
 *
 * Auftragstext, ausdruecklich abweichend von web_wms (dort ist die Grenze je Seite verschieden):
 * bis einschliesslich vier Positionen zeigt der Streifen ALLE Bilder, ab fuenf nur die ersten DREI
 * plus eine Kachel `+X` mit `X = Gesamtzahl - 3`.
 *
 * ================= Wer das Popup oeffnet und den Fokus zurueckgibt =================
 *
 * Dasselbe Muster wie `Artikelbild.tsx#Artikelbild`/`ArtikelbildOverlay`: DIESE Komponente haelt den
 * Offen-Zustand des Popups (`useState`) und den Fokus-Anker (`triggerRef`) - `PositionenPopup`
 * selbst kennt den ausloesenden Knopf nicht und soll es auch nicht muessen.
 */
import { useRef, useState, type JSX } from "react";
import type { Order, OrderItem } from "../../lib/types.ts";
import { Artikelbild } from "../Artikelbild.tsx";
import { PositionenPopup } from "./PositionenPopup.tsx";

/** Ab dieser Positionszahl zeigt der Streifen nur noch drei Bilder plus eine "+X"-Kachel. */
const STREIFEN_GRENZE = 4;

export interface ArtikelstreifenProps {
  order: Order;
}

export function Artikelstreifen({ order }: ArtikelstreifenProps): JSX.Element | null {
  const items = Array.isArray(order.items) ? (order.items as OrderItem[]) : [];
  const [popupOffen, setPopupOffen] = useState(false);
  const knopfRef = useRef<HTMLButtonElement>(null);

  // Keine Positionen -> gar kein Knopf, kein leerer Platzhalter (Auftragstext).
  if (!items.length) return null;

  const gesamt = items.length;
  const gezeigt = gesamt <= STREIFEN_GRENZE ? items : items.slice(0, 3);
  const rest = gesamt <= STREIFEN_GRENZE ? 0 : gesamt - 3;

  return (
    <>
      <button
        ref={knopfRef}
        type="button"
        className="artikelstreifen"
        // NACHBESSERUNG (Geringes 1): bei genau einer Position sagte das `aria-label` bislang
        // "1 Positionen anzeigen".
        aria-label={`${gesamt} Position${gesamt === 1 ? "" : "en"} anzeigen`}
        onClick={(ereignis) => {
          // Die Zeile darunter oeffnet den Drawer (`ZeitstrahlSeite.tsx`, `onOeffnen`) - der
          // Streifen darf das nicht mit ausloesen (Auftragstext, Testfall 6).
          ereignis.stopPropagation();
          // Reload-Bug: der Knopf steckt in einem echten `<a href>` (`Verweis`/react-router
          // `Link`, `ZeitstrahlSeite.tsx`). `stopPropagation()` allein haelt nur React-Handler
          // weiter oben in der Kette auf - NICHT die native Default-Aktion des Ankers (Navigation
          // ueber `href`), die an `event.defaultPrevented` haengt, nicht an Propagation. Ohne
          // `preventDefault()` navigiert der Browser hier nativ -> voller Seiten-Reload statt
          // Popup.
          ereignis.preventDefault();
          setPopupOffen(true);
        }}
      >
        {gezeigt.map((item, index) => {
          const menge = Number(item?.qty);
          const zeigeMenge = Number.isFinite(menge) && menge > 0;
          return (
            <span
              className="artikelstreifen-kachel"
              // NACHBESSERUNG (Feinschliff Punkt 2, Pruefung 5648ff6): `kLieferantenBestellungPos`
              // (nur bei JTL-Positionen, `alsOrderItem`) und `index` (Laufindex) waren EIN
              // Schluesselraum - eine JTL-Position mit `kLieferantenBestellungPos: 1` neben einer
              // manuellen Position (keine Kennung) an Index 1 bekamen beide den Schluessel `"1"`.
              // Getrennte Praefixe schliessen die Kollision aus.
              key={
                item?.kLieferantenBestellungPos != null
                  ? `pos-${item.kLieferantenBestellungPos}`
                  : `idx-${index}`
              }
            >
              <Artikelbild kArtikel={item?.kArtikel} name={item?.name} klickbar={false} hatBild={item?.hatBild} />
              {zeigeMenge ? <span className="artikelstreifen-menge">{menge}</span> : null}
            </span>
          );
        })}
        {rest > 0 ? <span className="artikelstreifen-rest">+{rest}</span> : null}
      </button>
      {popupOffen ? (
        <PositionenPopup
          order={order}
          onSchliessen={() => {
            setPopupOffen(false);
            knopfRef.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}
